import { ICameraVideoTrack, IMicrophoneAudioTrack, UID } from "agora-rtc-react";
import { RemoteStreams, SESSION_MODE } from "../../constants";
import { useDeepDiveContext } from "DeepDiveProvider";
import SimpleBar from "simplebar-react";
import { Layout } from "antd";
import { useMemo } from "react";
import LocalUserSteam from "./LocalUserStream";
import RemoteUserStream from "./RemoteUserStream";
import DynamicGrid from "components/layouts/DynamicGrid";

const { Sider, Content } = Layout;

const PinnedStreamLayout = ({
  localMicrophoneTrack,
  localCameraTrack,
  remoteStreams,
  sessionMode,
  cameraOn,
  micOn,
}: {
  localMicrophoneTrack: IMicrophoneAudioTrack | null;
  localCameraTrack: ICameraVideoTrack | null;
  remoteStreams: RemoteStreams;
  sessionMode: SESSION_MODE;
  cameraOn: boolean;
  micOn: boolean;
}) => {
  const { pinnedStreams, joinee, isRecorder } = useDeepDiveContext();

  const selfUserId = joinee?.uids?.cameraAudioUid as UID;
  const isSelfUserStreamPinned = pinnedStreams.includes(selfUserId);

  const {
    moderatorCamerasMics,
    moderatorExternalCamerasMics,
    moderatorScreens,
    participantCamerasMics,
    participantExternalCamerasMics,
    participantScreens,
    translatorCamerasMics,
    translatorExternalCamerasMics,
  } = remoteStreams;

  const pinnedModeratorCamerasMics = moderatorCamerasMics.filter((user) => pinnedStreams.includes(user.uid));
  const pinnedModeratorExternalCamerasMics = moderatorExternalCamerasMics.filter((user) => pinnedStreams.includes(user.uid));
  const pinnedModeratorScreens = moderatorScreens.filter((user) => pinnedStreams.includes(user.uid));
  const pinnedParticipantCamerasMics = participantCamerasMics.filter((user) => pinnedStreams.includes(user.uid));
  const pinnedParticipantExternalCamerasMics = participantExternalCamerasMics.filter((user) => pinnedStreams.includes(user.uid));
  const pinnedParticipantScreens = participantScreens.filter((user) => pinnedStreams.includes(user.uid));
  const pinnedTranslatorCamerasMics = translatorCamerasMics.filter((user) => pinnedStreams.includes(user.uid));
  const pinnedTranslatorExternalCamerasMics = translatorExternalCamerasMics.filter((user) => pinnedStreams.includes(user.uid));

  const nonPinnedModeratorCamerasMics = moderatorCamerasMics.filter((user) => !pinnedStreams.includes(user.uid));
  const nonPinnedModeratorExternalCamerasMics = moderatorExternalCamerasMics.filter((user) => !pinnedStreams.includes(user.uid));
  const nonPinnedModeratorScreens = moderatorScreens.filter((user) => !pinnedStreams.includes(user.uid));
  const nonPinnedParticipantCamerasMics = participantCamerasMics.filter((user) => !pinnedStreams.includes(user.uid));
  const nonPinnedParticipantExternalCamerasMics = participantExternalCamerasMics.filter((user) => !pinnedStreams.includes(user.uid));
  const nonPinnedParticipantScreens = participantScreens.filter((user) => !pinnedStreams.includes(user.uid));
  const nonPinnedTranslatorCamerasMics = translatorCamerasMics.filter((user) => !pinnedStreams.includes(user.uid));
  const nonPinnedTranslatorExternalCamerasMics = translatorExternalCamerasMics.filter((user) => !pinnedStreams.includes(user.uid));

  const sideRemoteStreams = useMemo(() => {
    const sideRemoteStreams = [
      ...nonPinnedParticipantScreens,
      ...nonPinnedParticipantCamerasMics,
      ...nonPinnedParticipantExternalCamerasMics,
      ...nonPinnedModeratorScreens,
      ...nonPinnedModeratorCamerasMics,
      ...nonPinnedModeratorExternalCamerasMics,
      ...nonPinnedTranslatorCamerasMics,
      ...nonPinnedTranslatorExternalCamerasMics,
    ];
    return sideRemoteStreams;
  }, [
    nonPinnedModeratorCamerasMics,
    nonPinnedModeratorExternalCamerasMics,
    nonPinnedModeratorScreens,
    nonPinnedParticipantCamerasMics,
    nonPinnedParticipantExternalCamerasMics,
    nonPinnedParticipantScreens,
    nonPinnedTranslatorCamerasMics,
    nonPinnedTranslatorExternalCamerasMics,
  ]);

  const centerStreams = useMemo(() => {
    const centerStreams = [
      ...pinnedParticipantScreens,
      ...pinnedParticipantCamerasMics,
      ...pinnedParticipantExternalCamerasMics,
      ...pinnedModeratorScreens,
      ...pinnedModeratorCamerasMics,
      ...pinnedModeratorExternalCamerasMics,
      ...pinnedTranslatorCamerasMics,
      ...pinnedTranslatorExternalCamerasMics,
    ];
    return centerStreams;
  }, [
    pinnedModeratorCamerasMics,
    pinnedModeratorExternalCamerasMics,
    pinnedModeratorScreens,
    pinnedParticipantCamerasMics,
    pinnedParticipantExternalCamerasMics,
    pinnedParticipantScreens,
    pinnedTranslatorCamerasMics,
    pinnedTranslatorExternalCamerasMics,
  ]);

  const isSideStreamsAvailable = useMemo(() => {
    const sideRemoteStreamsAvailable = sideRemoteStreams.length > 0;
    const toShowSelfInSide = !isRecorder && !isSelfUserStreamPinned;
    return sideRemoteStreamsAvailable || toShowSelfInSide;
  }, [isRecorder, isSelfUserStreamPinned, sideRemoteStreams.length]);

  return (
    <>
      <Layout className='h-100'>
        <Content className='p-2 bg-E3E8EF' style={{ width: `calc(100vw - ${isSideStreamsAvailable ? "230px - 2rem" : "0px"})` }}>
          <DynamicGrid>
            {centerStreams.map((user) => {
              return <RemoteUserStream isSideBar={true} user={user} className='middle-user' key={user.uid} />;
            })}
            {!isRecorder && isSelfUserStreamPinned && (
              <LocalUserSteam
                audioTrack={localMicrophoneTrack}
                cameraOn={cameraOn}
                micOn={micOn}
                videoTrack={localCameraTrack}
                userId={joinee?.uids?.cameraAudioUid as UID}
                userClassName='middle-user'
              />
            )}
          </DynamicGrid>
        </Content>
        {isSideStreamsAvailable && (
          <Sider width={230} className={`h-100 ${sessionMode === SESSION_MODE.FOCUS_GROUP ? "bg-EEF2F6" : "bg-E3E8EF"}`}>
            <SimpleBar className='h-100'>
              <div className='d-flex gap-2 p-2 flex-wrap flex-column'>
                {sideRemoteStreams.map((user) => {
                  return <RemoteUserStream isSideBar={true} user={user} key={user.uid} />;
                })}

                {!isRecorder && !isSelfUserStreamPinned && (
                  <LocalUserSteam
                    audioTrack={localMicrophoneTrack}
                    cameraOn={cameraOn}
                    micOn={micOn}
                    videoTrack={localCameraTrack}
                    userId={joinee?.uids?.cameraAudioUid as UID}
                  />
                )}
              </div>
            </SimpleBar>
          </Sider>
        )}
      </Layout>
    </>
  );
};

export default PinnedStreamLayout;
